import type { ReactNode } from "react";

/**
 * Groups a `<label>` and its control (`input`/`select`/`textarea`) into ONE
 * flex item (UX audit 2026-09-15, finding #1 CRITICAL + transversal pattern
 * #1/#2/#10). Every multi-field form in this codebase rendered its labels
 * and controls as bare SIBLINGS directly under a `display: flex; flex-wrap:
 * wrap` container (`base.css`'s `form` rule) — each label-text node and each
 * input became its own independently-wrapping flex item, so the browser's
 * wrap algorithm could (and did, on the real deployed panel) place a label
 * next to the WRONG input once the row didn't have room for the next pair.
 * A single-field form (e.g. a one-input search bar) never showed the bug
 * because there was nothing else to misalign with — only forms with 2+
 * fields broke.
 */
export function Field({
  label,
  htmlFor,
  children,
  className,
  hideLabel = false,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
  className?: string;
  /** Keeps the label in the accessibility tree but visually hides it (toolbar contexts). */
  hideLabel?: boolean;
}) {
  return (
    <div className={className ? `v2-field ${className}` : "v2-field"}>
      <label htmlFor={htmlFor} className={hideLabel ? "v2-u-visually-hidden" : undefined}>
        {label}
      </label>
      {children}
    </div>
  );
}
